import type { RouteRecordRaw } from 'vue-router'

import { registerCreditMessages } from './i18n'

import { useLocaleStore } from '@/shared/i18n'

/**
 * 11's settings CHILD route — appended to the `/settings` parent by `installCreditsSettingsNav`,
 * never composed by 05 into the app layout. The path stays bare so the URL is `/settings/credits`,
 * matching v3's `/workspace-settings/credits`.
 *
 * ⚠ NOT a `kind:'route'` entry, and nothing here needs one. ORP-18893 and contract 22 §3.2 both
 * justify adding a `kind` field to the registry with "the `Credits` entry (`route:'CreditSettings'`)
 * proves the registry needs `kind:'route'`". Measured against v3 `main`, that is false:
 * `grep -n "route:" src/composables/useWorkspaceSettings.js` finds NO nav item declaring a route,
 * the Credits entry is a plain `{name, value:'credits', icon, active:true}` at `:208-213`, and the
 * legacy `CreditSettings` router record (`router/routes/app.js:267-270`) is a bare REDIRECT to
 * `/workspace-settings/credits` with no component. `WorkspaceSettings.vue:28-29` renders
 * `<CreditSettings />` as an ordinary tab. So Credits has exactly Connections' shape, `kind` is not
 * needed, and contract 22 §3.2 should drop that sentence (recorded on ORP-19903).
 *
 * 22's kit needs 22's own messages (`workspaceSettings.pane.*`); those are registered by the
 * `/settings` PARENT's `beforeEnter`, which vue-router runs before this one.
 */
export const creditSettingsRoutes: RouteRecordRaw[] = [
  {
    path: 'credits',
    name: 'SettingsCredits',
    component: () => import('./components/CreditSettingsPane.vue'),
    meta: { titleKey: 'credits.settings.title', navKey: 'credits' },
    beforeEnter: () => registerCreditMessages(useLocaleStore().locale),
  },
]

/** Native authenticated billing surface; composed under the app layout by module 05. */
export const creditRoutes: RouteRecordRaw[] = [
  {
    path: 'compare-plans',
    name: 'ComparePlans',
    component: () => import('./components/ComparePlansPage.vue'),
    meta: { titleKey: 'credits.comparePlans.title', navKey: 'credits' },
    beforeEnter: () => registerCreditMessages(useLocaleStore().locale),
  },
  {
    path: 'downgrade',
    alias: '/continue-on-free',
    name: 'FreeTransition',
    component: () => import('./components/FreeTransitionPage.vue'),
    meta: { titleKey: 'credits.freeTransition.title', navKey: 'credits' },
    beforeEnter: () => registerCreditMessages(useLocaleStore().locale),
  },
]

/** Provider return outside the app chrome. Legacy query params remain the wire contract until
 *  ORP-19089 defines a stable machine `status` contract for payment failure/cancel states. */
export const billingReturnRoutes: RouteRecordRaw[] = [
  {
    path: '/billing/return',
    name: 'BillingReturn',
    component: () => import('./components/BillingReturnView.vue'),
    // `mcpAllowed` (ORP-21656): an MCP-only account pays through the same Stripe return.
    meta: {
      requiresAuth: false,
      layout: 'blank',
      mcpAllowed: true,
      titleKey: 'credits.billing.return.working',
    },
    beforeEnter: () => registerCreditMessages(useLocaleStore().locale),
  },
]
